import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { SwalToastService } from 'src/app/services/swal.service';
import { IAddCtParameters } from 'src/app/interfaces/CtParameters/IAddCtParameters';
import { CtParametersService } from 'src/app/services/ctparameters.service';

@Component({
  selector: 'app-ct-parameters-add-edit',
  standalone: true,
  templateUrl: './ct-parameters-add-edit.component.html',
  styleUrls: ['./ct-parameters-add-edit.component.css'],
  imports: [
    NzModalModule,
    TranslateModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NzSelectModule,
    CommonModule,
    NzButtonModule,
    NzTableModule,
    NzLayoutModule,
    NzIconModule,
  ],
})
export class CtParametersAddEditComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;
  isDropdownLoading = false;

  isEdit = false;
  editCtParametersId?: number;

  ctParametersSubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  ctParametersForm: FormGroup = new FormGroup({});

  constructor(
    private readonly ctParametersService: CtParametersService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isEdit = false;
    this.isLoading = true;
    this.isDropdownLoading = true;

    this.ctParametersForm = this.formBuilder.group({
      depthOfTracheaValue: [null, Validators.required],
      distanceFromSkinToTracheaValue: [null, Validators.required],
      distanceFromCricothyroidToCarinaValue: [null, Validators.required],
      distanceBetweenTrachealCartilagesValue: [null, Validators.required],
      anatomicalVariationsValue: [null, Validators.required],
    });

    this.ctParametersService.ctParametersEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.isLoading = false;

          this.editCtParametersId = data?.id;

          this.ctParametersForm.patchValue({
            depthOfTracheaValue: data?.depthOfTrachea,
            distanceFromSkinToTracheaValue: data?.distanceFromSkinToTrachea,
            distanceFromCricothyroidToCarinaValue:
              data?.distanceFromCricothyroidToCarina,
            distanceBetweenTrachealCartilagesValue:
              data?.distanceBetweenTrachealCartilages,
            anatomicalVariationsValue: data?.anatomicalVariations,
          });
        } else {
          this.isEdit = false;
          this.isLoading = false;

          this.editCtParametersId = undefined;

          this?.ctParametersForm.reset();
        }
      });

    this.ctParametersService.ctParametersModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    this.ctParametersForm.reset();

    this.ctParametersService.ctParametersModalSub.next(false);

    this.ctParametersSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.ctParametersSubmitted = true;

    if (this.ctParametersForm.valid) {
      const ctParametersObj: IAddCtParameters = {};

      ctParametersObj.id = this.editCtParametersId;

      ctParametersObj.depthOfTrachea = this.ctParametersForm.get(
        'depthOfTracheaValue'
      )?.value;
      ctParametersObj.distanceFromSkinToTrachea = this.ctParametersForm.get(
        'distanceFromSkinToTracheaValue'
      )?.value;
      ctParametersObj.distanceFromCricothyroidToCarina =
        this.ctParametersForm.get(
          'distanceFromCricothyroidToCarinaValue'
        )?.value;
      ctParametersObj.distanceBetweenTrachealCartilages =
        this.ctParametersForm.get(
          'distanceBetweenTrachealCartilagesValue'
        )?.value;
      ctParametersObj.anatomicalVariations = this.ctParametersForm.get(
        'anatomicalVariationsValue'
      )?.value;

      if (this.isEdit) {
        this.ctParametersService.editCtParameters(ctParametersObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              '3D-CT Parameters record edited successfully'
            );
            this.ctParametersService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError(
              'Unable to edit 3D-CT Parameters record'
            );
            this.handleCancel();
          },
        });
      } else {
        this.ctParametersService.addCtParameters(ctParametersObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              '3D-CT Parameters record added successfully'
            );
            this.ctParametersService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError(
              'Unable to add 3D-CT Parameters record'
            );
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }
}
